import React from 'react';
import { Container, Typography, Box, Grid, Paper, Button } from '@mui/material';
import { Upload, Search, UserCheck, BarChart3, Send } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface Step {
  icon: React.ReactNode;
  title: string;
  description: string;
  color: string;
}

const steps: Step[] = [
  {
    icon: <Upload size={40} />,
    title: 'Upload Requirements',
    description: 'Simply upload your job description or enter it interactively with RiC',
    color: '#3f51b5'
  },
  {
    icon: <Search size={40} />,
    title: 'AI Analysis',
    description: 'Our advanced AI analyzes the job description, extracting skills, experience, and qualifications',
    color: '#4caf50'
  },
  {
    icon: <UserCheck size={40} />,
    title: 'Candidate Search',
    description: 'Candidates are automatically "discovered" based on job requirements (1 billion+ profiles reviewed)',
    color: '#ffa500'
  },
  {
    icon: <BarChart3 size={40} />,
    title: 'Candidate Filtering',
    description: 'Advanced analytics using AI scoring and filtering of candidates according to the thresholds you set',
    color: '#00bcd4'
  },
  {
    icon: <Send size={40} />,
    title: 'Job Posting',
    description: 'Jobs are posted and distributed on up to 75+ industry leading job boards, giving you access to both active and passive candidates.',
    color: '#3f51b5'
  }
];

const HowItWorks: React.FC = () => {
  const navigate = useNavigate();

  const handleScheduleDemo = (): void => {
    navigate('/contact', { state: { subject: 'Demo Request' } });
  };

  return (
    <Box sx={{ py: 10 }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography 
            variant="overline" 
            component="div"
            sx={{ 
              color: 'primary.main',
              fontWeight: 600,
              fontSize: { xs: '2rem', md: '2.5rem' },
              letterSpacing: 1.5,
              mb: 1
            }}
          >
            SIMPLE PROCESS
          </Typography>
          <Typography 
            variant="h2" 
            component="h2"
            sx={{ 
              fontWeight: 700,
              mb: 2,
              fontSize: { xs: '2rem', md: '2.5rem' }
            }}
          >
            How RECRUITMENT INTELLIGENCE<span style={{ fontSize: '1.0em', verticalAlign: 'super' }}>®</span> Works
          </Typography>
          <Typography 
            variant="subtitle1"
            sx={{ 
              color: 'text.secondary',
              maxWidth: '800px',
              mx: 'auto',
              mb: 4
            }}
          >
            Our streamlined process makes it easy to implement AI-powered recruiting in your organization.
          </Typography>
        </Box>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          {steps.map((step, index) => (
            <Grid item xs={12} sm={6} md={2.4} key={index}>
              <Paper 
                elevation={3}
                sx={{ 
                  p: 3, 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  borderRadius: 4,
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-10px)',
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '4px',
                    backgroundColor: step.color,
                  }
                }}
              >
                <Box 
                  sx={{ 
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: `${step.color}15`,
                    color: step.color,
                    mb: 3
                  }}
                >
                  {step.icon}
                </Box>
                <Typography 
                  variant="h5" 
                  component="h3" 
                  gutterBottom
                  sx={{ fontWeight: 600 }}
                >
                  {index + 1}. {step.title}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ flexGrow: 1 }}>
                  {step.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      <Grid item xs={12}>
      <Paper 
        elevation={3}
        sx={{ 
          p: 3,
          mb: 18,
          borderRadius: 3,
          textAlign: 'center',
          backgroundColor: '#fafafa',
          borderTop: '5px solid #ff0000' 
        }}
      >
        <Typography variant="h5" color="red" sx={{ mb: 2 }}>
          <strong>Our Hiring Guarantee</strong>
        </Typography>
        <Typography variant="body1" color="text.secondary">
          If you don't make a hire using our Recruitment Intelligence® platform, we'll credit you <strong>$500 toward a fully managed search</strong> led by our <strong>Forbes-ranked professional recruitment team.</strong> With over <strong>10 years of proven success</strong> and a <strong>93.8%</strong> hire rate, companies trust our hybrid AI + human solution to deliver top talent - risk free.  You have nothing to lose, and only exceptional candidates to gain
        </Typography>
      </Paper>
    </Grid>

{/*         <Box
                component="video"
                autoPlay
                muted
                loop
                playsInline
                sx={{
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                }}
              >
                <source src="/assets/HeroHuntMod3.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </Box>
 */}
        <Box 
          sx={{ 
            backgroundColor: 'primary.main',
            borderRadius: 4,
            p: { xs: 4, md: 6 },
            color: 'white',
            textAlign: 'center',
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Background decoration */}
          <Box 
            sx={{
              position: 'absolute',
              top: '-10%',
              right: '-5%',
              width: '300px',
              height: '300px',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
              zIndex: 0,
            }}
          />
          <Box 
            sx={{
              position: 'absolute',
              bottom: '-15%',
              left: '-10%',
              width: '400px',
              height: '400px',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
              zIndex: 0,
            }}
          />
          
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography 
              variant="h3" 
              component="h3" 
              gutterBottom
              sx={{ 
                fontWeight: 700,
                mb: 3,
                fontSize: { xs: '1.75rem', md: '2.25rem' }
              }}
            >
              Ready to Transform Your Hiring Process?
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 4, 
                fontWeight: 400,
                opacity: 0.9,
                maxWidth: '800px',
                mx: 'auto'
              }}
            >
              Join thousands of companies that have revolutionized their recruitment with RECRUITMENT INTELLIGENCE<span style={{ fontSize: '1.0em', verticalAlign: 'super' }}>®</span>
            </Typography>
            <Button 
              variant="contained" 
              color="secondary" 
              size="large"
              onClick={handleScheduleDemo}
              sx={{ 
                py: 1.5,
                px: 4,
                boxShadow: '0 4px 14px 0 rgba(245, 0, 87, 0.39)',
              }}
            >
              Schedule a Demo
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default HowItWorks;
